import React, {useState} from 'react';
import {Box, Typography, Grid} from '@mui/material';
import IndividualDashboardShell from '../components/IndividualDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, PageHeader, ScoreRow, BenchmarkLegend, ListCard, GuidanceBanner} from '../components/widgets.comp';
import { useDashboard } from '../../../context/DashboardContext';
import { READINESS_BAND_COLORS } from '../constants';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortItems} from '../../DashboardCommon/barSort';

const COMPETENCIES_SECTION_CODE = 'S_3';
const CARD_SECTIONS = [COMPETENCIES_SECTION_CODE];

const CompetenciesPage = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });
    // This page has always ranked the areas highest first, so that stays the opening view;
    // "Group order" is the card's own area order (QA 9 Oct).
    const [sortMode, setSortMode] = useState('desc');

    const s3Section = dashboardData?.cards?.sections?.find(s => s.section_code === COMPETENCIES_SECTION_CODE);

    if (loading) {
        return (
            <IndividualDashboardShell>
                <CircularLoader />
            </IndividualDashboardShell>
        );
    }

    if (!dashboardData || !s3Section) {
        return (
            <IndividualDashboardShell>
                <NoDataAvailable />
            </IndividualDashboardShell>
        );
    }

    const s3Cards = s3Section?.cards || [];

    const c1Card = s3Cards.find(c => c.card_type === 'TitleCard');
    const c2Card = s3Cards.find(c => c.card_type === 'BarChartBMCard');
    const c3Card = s3Cards.find(c => c.card_code === 'C_3');

    // Mockup titles (ID slide 80) when the card sends none.
    const title = c1Card?.title || "My Online Safety Readiness";
    const subtitle = c1Card?.footer || c1Card?.header || "What are my strengths and growth areas?";

    const readinessItems = c2Card?.items || [];

    const sortedItems = [...readinessItems].sort((a, b) => b.value - a.value);

    // Rubric-band membership, not top/bottom-N ranking — every "High" area is
    // a strength and every "Low" area is a growth focus, however many there are.
    // The datahouse sends both lists (ID.S_3.C_2a / C_2b); the band filter is
    // the fallback when a card is missing.
    const c2aCard = s3Cards.find(c => c.card_code === 'C_2a');
    const c2bCard = s3Cards.find(c => c.card_code === 'C_2b');
    const asLine = (item) => `${item.label} (${Math.trunc(Number(item.value))})`;
    const finalStrengths = (c2aCard ? (c2aCard.items || []) : readinessItems.filter(item => item.band === 'High')).map(asLine);
    const finalGrow = (c2bCard ? (c2bCard.items || []) : readinessItems.filter(item => item.band === 'Low')).map(asLine);

    const getGuidanceData = () => {
        // No guidance card: build the mockup's sentence from the scores so the
        // box is never blank (ID slide 79).
        if (!c3Card || !c3Card.leftline_html) {
            if (!sortedItems.length) return { label: '', text: '' };
            const names = sortedItems.slice(0, 3).map(it => it.label);
            const strongest = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
            const weakest = sortedItems[sortedItems.length - 1]?.label;
            return {
                label: 'Decision guidance:',
                text: `Your strengths are ${strongest}. A good area to grow is ${weakest} — small, regular practice makes the biggest difference.`,
            };
        }
        let rawHtml = c3Card.leftline_html;
        const h1Match = rawHtml.match(/<h1>([\s\S]*?)<\/h1>/i);
        let label = h1Match ? h1Match[1] : '';

        const pMatch = rawHtml.match(/<p>([\s\S]*?)<\/p>/i);
        let text = pMatch ? pMatch[1] : rawHtml.replace(/<[^>]*>/g, '');

        const strongestNames = sortedItems.slice(0, 3).map(it => it.label);
        const strongestText = strongestNames.length > 1
            ? strongestNames.slice(0, -1).join(', ') + ' and ' + strongestNames[strongestNames.length - 1]
            : strongestNames[0] || '';

        const weakestText = sortedItems[sortedItems.length - 1]?.label || '';

        text = text
            .replace('{strongest_areas}', strongestText)
            .replace('{weakest_area}', weakestText);

        return { label, text };
    };

    const guidance = getGuidanceData();

    const getAreaLabel = (item, index) => {
        const match = item.sublabel?.match(/\d+/);
        const num = match ? match[0] : index + 1;
        return `Readiness Area ${num}`;
    };
    // Each area keeps the label of its place in the card, whichever order it is shown in.
    const shownItems = sortItems(readinessItems.map((it, idx) => ({...it, area: getAreaLabel(it, idx)})), sortMode);

    return (
        <IndividualDashboardShell>
            <PageHeader
                title={title}
                subtitle={subtitle}
            />

            <Grid container spacing={2.5} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{c2Card?.title || "Competencies Area Scores"}</Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                <BenchmarkLegend bandColorMap={READINESS_BAND_COLORS}/>
                                <SortToggle value={sortMode} onChange={setSortMode}/>
                            </Box>
                        </Box>
                        {shownItems.map((it) => (
                            <ScoreRow
                                key={it.code}
                                area={it.area}
                                label={it.label}
                                score={Math.trunc(Number(it.value))}
                                band={it.band}
                                marker={it.benchmark_values?.find(b => b.name === 'Class avg.')?.value}
                                labelWidth={{xs: 170, md: 260}}
                                bandColorMap={READINESS_BAND_COLORS}
                            />
                        ))}
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <ListCard title="Your strengths" items={finalStrengths} color="#1B6B55"
                              emptyText="No area is in the High band yet — keep practising to build one."/>
                    <Box sx={{mt: 2.5}}>
                        <ListCard title="Areas to grow" items={finalGrow} color="#E04535"
                                  emptyText="No area is in the Low band — keep up the good work."/>
                    </Box>
                    {guidance.text && <GuidanceBanner label={guidance.label} text={guidance.text}/>}
                </Grid>
            </Grid>
        </IndividualDashboardShell>
    );
};

export default CompetenciesPage;

